import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Package, 
  Truck, 
  CheckCircle,
  ArrowRight,
  ChefHat,
  Key,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ProductCollection",
    "name": "Produits LST OIL SA - Sénégal et International",
    "description": "Gamme complète de produits pétroliers, alimentaires et matériaux BTP au Sénégal, Afrique de l'Ouest et international",
    "provider": {
      "@type": "Organization",
      "name": "LST OIL SA",
      "url": "https://www.lstoilsa.com"
    },
    "areaServed": [
      { "@type": "Country", "name": "Sénégal" },
      { "@type": "Country", "name": "Mali" },
      { "@type": "Country", "name": "Côte d'Ivoire" },
      { "@type": "Country", "name": "Congo" },
      { "@type": "Country", "name": "Monde" }
    ]
  };


const Products = () => {
  const [activeCategory, setActiveCategory] = useState('bases');
  const [expandedCategories, setExpandedCategories] = useState({});
  const location = useLocation();

  // Fonction améliorée pour gérer la navigation par hash
  const handleHashNavigation = (hash) => {
    if (!hash) return;

    let categoryToActivate = activeCategory;
    let subcategoryToExpand = null;

    // Mapping des hash vers les catégories et sous-catégories
    if (hash === 'bases' || hash === 'petrole' || hash === 'btp') {
      categoryToActivate = 'bases';
      if (hash === 'petrole') subcategoryToExpand = 'petrole';
      if (hash === 'btp') subcategoryToExpand = 'btp';
      if (hash === 'bases') {
        // Pour "Secteurs Clés" général, ouvrir la première sous-catégorie
        subcategoryToExpand = 'petrole';
      }
    }
    if (hash === 'alimentaires' || hash === 'denrees' || hash === 'halieutiques') {
      categoryToActivate = 'alimentaires';
      if (hash === 'denrees') subcategoryToExpand = 'denrees';
      if (hash === 'halieutiques') subcategoryToExpand = 'halieutiques';
      if (hash === 'alimentaires') {
        // Pour "Produits Alimentaires" général, ouvrir la première sous-catégorie
        subcategoryToExpand = 'denrees';
      }
    }
    if (hash === 'general') {
      categoryToActivate = 'general';
    }

    // Changement de catégorie si nécessaire
    if (categoryToActivate !== activeCategory) {
      setActiveCategory(categoryToActivate);
      
      // Attendre que la catégorie soit changée avant de gérer l'expansion et le scroll
      setTimeout(() => {
        if (subcategoryToExpand) {
          setExpandedCategories(prev => ({
            [subcategoryToExpand]: true // Fermer les autres, ouvrir seulement celle demandée
          }));
        }
        
        // Scroll vers l'élément après un délai supplémentaire
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) {
            const yOffset = -100; // Compensation pour le header fixe
            const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }, 400);
      }, 200);
    } else {
      // Même catégorie, gérer directement l'expansion et le scroll
      if (subcategoryToExpand) {
        setExpandedCategories(prev => ({
          [subcategoryToExpand]: true // Fermer les autres, ouvrir seulement celle demandée
        }));
      }
      
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) {
          const yOffset = -100; // Compensation pour le header fixe
          const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 400);
    }
  };

  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    if (hash) {
      handleHashNavigation(hash);
    }
  }, [location.hash]);

  // Réinitialiser l'état de navigation quand on change de catégorie manuellement
  useEffect(() => {
    // Fermer toutes les catégories expandées quand on change de catégorie
    setExpandedCategories({});
  }, [activeCategory]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const categories = [
    {
      id: 'general',
      name: 'Tous nos Produits',
      icon: Package,
      description: 'Vue d\'ensemble de tous nos produits',
      hasSubcategories: false
    },
    {
      id: 'bases',
      name: 'Hydrocarbures & BTP',
      icon: Key,
      description: 'Hydrocarbures et matériaux BTP',
      hasSubcategories: true,
      subcategories: ['Hydrocarbures', 'BTP & Matériaux']
    },
    {
      id: 'alimentaires',
      name: 'Produits Alimentaires',
      icon: ChefHat,
      description: 'Denrées alimentaires et produits halieutiques',
      hasSubcategories: true,
      subcategories: ['Denrées Première Nécessité', 'Produits Halieutiques']
    }
  ];

  const products = {
    bases: {
      petrole: [
        {
          id: 1,
          name: 'Essence Premium',
          description: 'Essence de haute qualité pour véhicules et équipements',
          specifications: {
            type: 'Essence',
            octane: '95-98',
            origine: 'Moyen-Orient',
            certification: 'ISO 9001'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/essence.png',
          tags: ['Essence', 'Premium', 'Véhicules']
        },
        {
          id: 2,
          name: 'Gasoil Standard',
          description: 'Gasoil de qualité industrielle pour équipements lourds',
          specifications: {
            type: 'Gazole',
            densité: '0.82-0.85',
            origine: 'Afrique de l\'Ouest',
            certification: 'ISO 9001'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/gazole.jpg',
          tags: ['Gazole', 'Industriel', 'Équipements']
        },
        {
          id: 3,
          name: 'Huile Moteur 15W-40',
          description: 'Huile moteur haute performance pour véhicules et machines',
          specifications: {
            type: 'Huile Moteur',
            viscosité: '15W-40',
            origine: 'Europe',
            certification: 'API SN'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/essencemoteur.png',
          tags: ['Huile', 'Moteur', 'Performance']
        }
      ],
      btp: [
        {
          id: 10,
          name: 'Ciment Portland',
          description: 'Ciment de haute qualité pour construction',
          specifications: {
            type: 'Ciment',
            résistance: '42.5 MPa',
            origine: 'Sénégal',
            certification: 'NF EN 197-1'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/ciment.jpg',
          tags: ['Ciment', 'Construction', 'BTP']
        },
        {
          id: 11,
          name: 'Acier de Construction',
          description: 'Barres d\'acier pour armatures et structures',
          specifications: {
            type: 'Acier',
            diamètre: '6-32mm',
            origine: 'International',
            certification: 'ISO 6935'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/fer.jpeg',
          tags: ['Acier', 'Armature', 'Structure']
        },
        {
          id: 9,
          name: 'Équipements Industriels',
          description: 'Équipements industriels neufs et d\'occasion pour tous secteurs',
          specifications: {
            type: 'Équipements',
            état: 'Neuf/Occasion',
            secteur: 'Tous secteurs',
            origine: 'International'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/materiel.png',
          tags: ['Équipements', 'Industriel', 'Machines']
        }
      ]
    },
    alimentaires: {
      denrees: [
        {
          id: 4,
          name: 'Sucre Blanc',
          description: 'Sucre de qualité supérieure pour l\'industrie alimentaire',
          specifications: {
            type: 'Sucre Blanc',
            pureté: '99.8%',
            origine: 'Sénégal',
            certification: 'HACCP'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/sucre.png',
          tags: ['Sucre', 'Alimentaire', 'Local']
        },
        {
          id: 5,
          name: 'Riz de la Vallée',
          description: 'Riz de qualité pour l\'export et la consommation locale',
          specifications: {
            type: 'Riz Blanc',
            grade: 'Grade A',
            origine: 'Vallée du Sénégal',
            certification: 'Fair Trade'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/riz.png',
          tags: ['Riz', 'Vallée', 'Local']
        },
        {
          id: 6,
          name: 'Huile Végétale',
          description: 'Huile raffinée pour l\'industrie alimentaire',
          specifications: {
            type: 'Huile de Palme',
            raffinage: 'Raffinée',
            origine: 'Afrique de l\'Ouest',
            certification: 'HACCP'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/huile.png',
          tags: ['Huile', 'Raffinée', 'Alimentaire']
        }
      ],
      halieutiques: [
        {
          id: 7,
          name: 'Poisson Frais',
          description: 'Poisson fraîche et congelé de qualité supérieure',
          specifications: {
            type: 'Poisson Frais',
            conservation: 'Frais/Congelé',
            origine: 'Maroc',
            certification: 'HACCP'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/poisson.jpg',
          tags: ['Poisson', 'Halieutique', 'Frais']
        },
        {
          id: 8,
          name: 'Crevettes',
          description: 'Crevettes fraîches et congelées de qualité supérieure',
          specifications: {
            type: 'Crevettes',
            conservation: 'Frais/Congelé',
            origine: 'Maroc',
            certification: 'HACCP'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/crevettes.jpg',
          tags: ['Crevette', 'Halieutique', 'Frais']
        },
        {
          id: 9,
          name: 'Poulpe',
          description: 'Poulpes fraîches et congelées de qualité supérieure',
          specifications: {
            type: 'Poulpes',
            conservation: 'Frais/Congelé',
            origine: 'Maroc',
            certification: 'HACCP'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/poulpe.png',
          tags: ['Poulpe', 'Halieutique', 'Frais']
        }
      ]
    },
    general: // Tous nos produits regroupés
      [
        // Produits pétroliers
        {
          id: 1,
          name: 'Essence Premium',
          category: 'petroleum',
          description: 'Essence de haute qualité pour véhicules et équipements',
          specifications: {
            type: 'Essence',
            octane: '95-98',
            origine: 'Moyen-Orient',
            certification: 'ISO 9001'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/essence.png',
          tags: ['Essence', 'Premium', 'Véhicules']
        },
        {
          id: 2,
          name: 'Gasoil Standard',
          category: 'petroleum',
          description: 'Gasoil de qualité industrielle pour équipements lourds',
          specifications: {
            type: 'Gazole',
            densité: '0.82-0.85',
            origine: 'Afrique de l\'Ouest',
            certification: 'ISO 9001'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/gazole.jpg',
          tags: ['Gazole', 'Industriel', 'Équipements']
        },
        {
          id: 3,
          name: 'Huile Moteur 15W-40',
          category: 'petroleum',
          description: 'Huile moteur haute performance pour véhicules et machines',
          specifications: {
            type: 'Huile Moteur',
            viscosité: '15W-40',
            origine: 'Europe',
            certification: 'API SN'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/essencemoteur.png',
          tags: ['Huile', 'Moteur', 'Performance']
        },
        // Produits alimentaires - denrées
        {
          id: 4,
          name: 'Sucre Blanc',
          category: 'nutritional',
          description: 'Sucre de qualité supérieure pour l\'industrie alimentaire',
          specifications: {
            type: 'Sucre Blanc',
            pureté: '99.8%',
            origine: 'Sénégal',
            certification: 'HACCP'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/sucre.png',
          tags: ['Sucre', 'Alimentaire', 'Local']
        },
        {
          id: 5,
          name: 'Riz de la Vallée',
          category: 'nutritional',
          description: 'Riz de qualité pour l\'export et la consommation locale',
          specifications: {
            type: 'Riz Blanc',
            grade: 'Grade A',
            origine: 'Vallée du Sénégal',
            certification: 'Fair Trade'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/riz.png',
          tags: ['Riz', 'Vallée', 'Local']
        },
        {
          id: 6,
          name: 'Huile Végétale',
          category: 'nutritional',
          description: 'Huile raffinée pour l\'industrie alimentaire',
          specifications: {
            type: 'Huile de Palme',
            raffinage: 'Raffinée',
            origine: 'Afrique de l\'Ouest',
            certification: 'HACCP'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/huile.png',
          tags: ['Huile', 'Raffinée', 'Alimentaire']
        },
        // Produits halieutiques
        {
          id: 7,
          name: 'Poisson Frais',
          category: 'livestock',
          description: 'Poisson fraîche et congelé de qualité supérieure',
          specifications: {
            type: 'Poisson Frais',
            conservation: 'Frais/Congelé',
            origine: 'Côtes Sénégalaises',
            certification: 'HACCP'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/poisson.jpg',
          tags: ['Poisson', 'Halieutique', 'Frais']
        },
        {
          id: 8,
          name: 'Crevettes',
          category: 'livestock',
          description: 'Crevettes fraîches et congelées de qualité supérieure',
          specifications: {
            type: 'Crevettes',
            conservation: 'Frais/Congelé',
            origine: 'Mauritanie',
            certification: 'HACCP'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/crevettes.jpg',
          tags: ['Crevette', 'Halieutique', 'Frais']
        },
        {
          id: 9,
          name: 'Poulpe',
          description: 'Poulpes fraîches et congelées de qualité supérieure',
          specifications: {
            type: 'Poulpes',
            conservation: 'Frais/Congelé',
            origine: 'Mauritanie',
            certification: 'HACCP'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/poulpe.png',
          tags: ['Poulpe', 'Halieutique', 'Frais']
        },
        // Produits BTP et équipements
        {
          id: 10,
          name: 'Équipements Industriels',
          category: 'general',
          description: 'Équipements industriels neufs et d\'occasion pour tous secteurs',
          specifications: {
            type: 'Équipements',
            état: 'Neuf/Occasion',
            secteur: 'Tous secteurs',
            origine: 'International'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/materiel.png',
          tags: ['Équipements', 'Industriel', 'Machines']
        },
        {
          id: 11,
          name: 'Ciment Portland',
          category: 'general',
          description: 'Ciment de haute qualité pour construction',
          specifications: {
            type: 'Ciment',
            résistance: '42.5 MPa',
            origine: 'Sénégal',
            certification: 'NF EN 197-1'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/ciment.jpg',
          tags: ['Ciment', 'Construction', 'BTP']
        },
        {
          id: 12,
          name: 'Acier de Construction',
          category: 'general',
          description: 'Barres d\'acier pour armatures et structures',
          specifications: {
            type: 'Acier',
            diamètre: '6-32mm',
            origine: 'International',
            certification: 'ISO 6935'
          },
          price: 'Prix sur demande',
          stock: 'Disponible',
          image: '/images/products/fer.jpeg',
          tags: ['Acier', 'Armature', 'Structure']
        }
      ]
  };

  const renderProducts = () => {
    if (activeCategory === 'general') {
      return (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.general.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>
      );
    }

    const currentProducts = products[activeCategory];
    
    return (
      <div className="space-y-8">
        {Object.entries(currentProducts).map(([subcategoryKey, productList]) => {
          const isExpanded = expandedCategories[subcategoryKey];
          const subcategoryTitle = 
            subcategoryKey === 'petrole' ? 'Hydrocarbures' :
            subcategoryKey === 'btp' ? 'BTP & Matériaux' :
            subcategoryKey === 'denrees' ? 'Denrées Première Nécessité' :
            subcategoryKey === 'halieutiques' ? 'Produits Halieutiques' : subcategoryKey;

          return (
            <div key={subcategoryKey} className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleCategory(subcategoryKey)}
                className="w-full px-6 py-4 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                id={subcategoryKey}
              >
                <h3 className="text-xl font-semibold text-gray-900">{subcategoryTitle}</h3>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="p-6 bg-white"
                >
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {productList.map((product, index) => (
                      <ProductCard key={product.id} product={product} index={index} />
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const ProductCard = ({ product, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden border border-gray-100"
    >
      {/* Product Image */}
      <div className="h-48 overflow-hidden">
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div 
          className="h-48 bg-gradient-to-br from-[#6BB8B1] to-[#3A7C76] flex items-center justify-center"
          style={{ display: 'none' }}
        >
          <Package className="w-16 h-16 text-[#3A7C76]" />
        </div>
      </div>

      {/* Product Info */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <span className={`text-sm font-medium px-2 py-1 rounded-full ${
            product.stock === 'Disponible' 
              ? 'text-green-600 bg-green-100' 
              : 'text-red-600 bg-red-100'
          }`}>
            {product.stock}
          </span>
        </div>

        <h4 className="text-lg font-semibold text-gray-900 mb-3">
          {product.name}
        </h4>
        <p className="text-gray-600 mb-4 leading-relaxed text-sm">
          {product.description}
        </p>

        {/* Specifications */}
        <div className="mb-4">
          <h5 className="text-sm font-semibold text-gray-700 mb-2">Spécifications :</h5>
          <div className="space-y-1">
            {Object.entries(product.specifications).slice(0, 3).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2 text-xs">
                <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                <span className="text-gray-600">
                  <span className="font-medium">{key}:</span> {value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {product.tags.map((tag, tagIndex) => (
            <span
              key={tagIndex}
              className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Price and CTA */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-[#3a7c76]">
            {product.price}
          </span>
          <Link to="/contact" className="bg-[#cd3127] hover:bg-[#cd3127]/90 text-white font-medium py-2 px-3 rounded-lg text-xs sm:text-sm transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-1">
            Devis
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </motion.div>
  );

  return (
    <>
      <Helmet>
        <title>Produits LST OIL SA | Hydrocarbures, Alimentaires & BTP Sénégal, Afrique, International</title>
        <meta name="description" content="Produits LST OIL SA : hydrocarbures, denrées alimentaires, produits halieutiques et matériaux BTP. Qualité certifiée au Sénégal, Afrique de l'Ouest et international." />
        <meta name="keywords" content="LST OIL SA, produits Sénégal, hydrocarbures Afrique, denrées alimentaires, produits halieutiques, matériaux BTP, essence Sénégal, gasoil Afrique, riz vallée, poisson frais, ciment Sénégal" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-16 md:py-20 bg-gradient-to-br from-[#6BB8B1] via-[#4B9D96] to-[#3A7C76] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-8 md:mb-6 font-heading">
              Nos Produits
            </h1>
            <p className="text-lg md:text-2xl text-white max-w-4xl mx-auto leading-relaxed">
              Une gamme complète de produits de qualité pour répondre à tous vos besoins
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Categories Tabs */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          {/* Anchors for hash navigation - IMPORTANT: Ces IDs doivent correspondre exactement aux liens du header */}
          <div id="bases" className="pt-24 -mt-24" />
          <div id="petrole" className="pt-24 -mt-24" />
          <div id="btp" className="pt-24 -mt-24" />
          <div id="alimentaires" className="pt-24 -mt-24" />
          <div id="denrees" className="pt-24 -mt-24" />
          <div id="halieutiques" className="pt-24 -mt-24" />
          <div id="general" className="pt-24 -mt-24" />

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10 md:mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
              Nos Gammes de Produits
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Des produits de qualité dans nos domaines d'expertise
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => {
                  setActiveCategory(category.id);
                  // Fermer toutes les catégories expandées quand on change d'onglet
                  setExpandedCategories({});
                }}
                className={`flex items-center gap-3 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-[#6BB8B1] to-[#4B9D96] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <category.icon className="w-5 h-5" />
                {category.name}
              </motion.button>
            ))}
          </div>

          {/* Products Content with Dropdowns */}
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {renderProducts()}
          </motion.div>
        </div>
      </section>

      {/* Product Categories Overview */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
              Pourquoi Choisir Nos Produits ?
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Une expertise reconnue dans plusieurs domaines d'activité
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100"
              >
                <div className="bg-[#6bb8b1] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <category.icon className="w-10 h-10 text-[#3a7c76]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {category.name}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {category.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#6BB8B1] to-[#3A7C76] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 font-heading">
              Besoin de Produits Spécifiques ?
            </h2>
            <p className="text-lg md:text-xl text-white mb-8 max-w-3xl mx-auto leading-relaxed">
              Contactez-nous pour des produits sur mesure ou des quantités importantes. 
              Notre équipe vous accompagne dans vos projets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
                to="/contact"
                className="bg-[#cd3127] hover:bg-[#cd3127]/90 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg inline-flex items-center gap-2"
              >
                Demander un Devis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="tel:+221338028484"
                className="border-2 border-white text-white hover:bg-white hover:text-[#4b9d96] font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-2"
              >
                <Truck className="w-5 h-5" />
                Nous Contacter
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Products;
